import { OrderFilterCheckboxes } from 'components/OrderFilterCheckboxes/OrderFilterCheckboxes';
import { OrderFilterOptions } from 'const/OrderFilterOptions';
import React from 'react';
import { FilterType } from '../Planner';
import {
  PlannerBundle,
  PlannerOrder,
  WindowGroups,
} from '../utils/usePlannerData';
import {
  BundleStatus,
  BundleStatus as BundleStatusType,
} from 'types/bundleTypes';
import { PlannerFilterBundleStatus } from '../Filter/BundleStatus/BundleStatus';
import { PlannerFilterWindow } from '../Filter/Window/Window';
import { PlannerFilterStore } from '../Filter/Store/Store';
import { PlannerProductType } from '../ProductType/ProductType';
import { ProductType } from 'types/orderTypes';

interface FilterProps {
  values: FilterType;
  orders?: PlannerOrder[][];
  bundles?: PlannerBundle[];
  fetchedOrders?: PlannerOrder[];
  windowGroups?: WindowGroups;
  onChange: (newFilters: FilterType) => void;
  onReset: () => void;
}

export const PlannerToolsFilter = ({ ...props }: FilterProps) => {
  const filters = props.values;

  const handleFilterUpdate = <Filter extends keyof FilterType>(
    filter: Filter,
    value: FilterType[Filter]
  ) => {
    const filterCopy = { ...props.values };
    filterCopy[filter] = value;
    props.onChange(filterCopy);
  };

  const handleCheckboxFilter = activeCheckboxFilters => {
    const newFilter = { ...filters, ...activeCheckboxFilters };
    props.onChange(newFilter);
  };

  const onBundleStatusFilterChange = (
    newBundleStatuses: BundleStatusType[]
  ) => {
    handleFilterUpdate('bundleStatus', newBundleStatuses);
  };

  const onWindowsFilterChange = (newWindows: string[]) => {
    handleFilterUpdate('windows', newWindows);
  };

  const onStoreFilterChange = (newStores: string[]) => {
    handleFilterUpdate('stores', newStores);
  };

  const orders = props.orders ?? [];
  const bundles = props.bundles ?? [];
  const fetchedOrders = props.fetchedOrders ?? [];
  const windowGroups: WindowGroups = props.windowGroups;

  const availableStatuses = [
    {
      name: 'In draft',
      values: [BundleStatus.Draft],
    },
    {
      name: 'Published',
      values: [BundleStatus.Published, BundleStatus.Proposed],
    },
    {
      name: 'In delivery',
      values: [BundleStatus.Accepted],
    },
  ];

  const onProductUpdate = (product: ProductType) => {
    handleFilterUpdate('productType', product);
  };

  return (
    <>
      <PlannerProductType
        value={props.values.productType}
        onUpdate={onProductUpdate}
      />
      <div className="filter">
        <PlannerFilterBundleStatus
          bundles={bundles}
          availableStatuses={availableStatuses}
          selectedStatuses={props.values.bundleStatus}
          onChange={onBundleStatusFilterChange}
          onClearAllFilters={props.onReset}
        />

        <div className="orders-header">
          <strong>Orders per window</strong>
        </div>
        <div className="tools-line" />
        <PlannerFilterWindow
          bundles={bundles}
          orders={orders}
          windowGroups={windowGroups}
          fetchedOrders={fetchedOrders}
          selectedWindows={props.values.windows}
          onChange={onWindowsFilterChange}
        />

        <div className="filter-header">
          <strong>Filter</strong>
        </div>
        <div className="tools-line" />
        <OrderFilterCheckboxes
          defaultValue={filters}
          onFilterChange={handleCheckboxFilter}
          type="column"
        />
        {filters[OrderFilterOptions.WITH_TEMP_CONTROL] &&
          filters[OrderFilterOptions.WITHOUT_TEMP_CONTROL] && (
            <div
              style={{
                background: '#Facccc',
                fontWeight: 500,
                border: '1px solid red',
                borderRadius: '2px',
                padding: '5px',
                marginBottom: '5px',
              }}
            >
              <div style={{ color: 'red' }}>Warning:</div>
              <div>
                Selecting both temperature control options will hide all orders
              </div>
            </div>
          )}
        <strong>Retailers</strong>
        <PlannerFilterStore
          bundles={bundles}
          orders={orders}
          selectedStores={filters.stores}
          onChange={onStoreFilterChange}
        />
      </div>
    </>
  );
};
